import api from "../apiClient";

// Backend source of truth: crm_backend/analytics_app/urls.py (mounted at /api/analytics/*).
// These helpers keep the UI resilient: callers should expect some endpoints to
// return empty payloads or 500s while FieldRoutes backfills are in progress.

export const getAnalyticsOverview = (params = {}) =>
  api.get("/analytics/overview/", { params });

export const getRevenueInsights = (params = {}) =>
  api.get("/analytics/revenue-insights/", { params });

// Proposed actions — AI proposes → owner approves → existing executor runs.
// SINGLE source of truth for this endpoint family. Backend contract:
// crm_backend/analytics_app/urls.py → path("proposed-actions/") under api/analytics/.
// Every caller imports these functions; the URL literal exists in exactly ONE place
// so a second file can never spell a divergent path (that was the recurrence risk).
export const PROPOSED_ACTIONS_ENDPOINT = "/analytics/proposed-actions/";

export const getProposedActions = (params = {}) =>
  api.get(PROPOSED_ACTIONS_ENDPOINT, { params });

export const approveProposedAction = (proposalId) =>
  api.post(`${PROPOSED_ACTIONS_ENDPOINT}${proposalId}/approve/`, {});

export const dismissProposedAction = (proposalId) =>
  api.post(`${PROPOSED_ACTIONS_ENDPOINT}${proposalId}/dismiss/`, {});

// Outreach engine review snapshot — same single-source rule as proposed
// actions. Backend: analytics_app/urls.py → contextualization/run/ (GET,
// mode=outreach_snapshot) and outreach/snapshots/<id>/approve/.
export const OUTREACH_SNAPSHOT_ENDPOINT = "/analytics/contextualization/run/";
export const OUTREACH_SNAPSHOT_APPROVE_ENDPOINT = (snapshotId) =>
  `/analytics/outreach/snapshots/${snapshotId}/approve/`;

export const getOutreachSnapshot = (definition = "quote_recovery") =>
  api.get(OUTREACH_SNAPSHOT_ENDPOINT, {
    params: { mode: "outreach_snapshot", definition },
  });

export const approveOutreachSnapshot = (snapshotId, exclude = []) =>
  api.post(OUTREACH_SNAPSHOT_APPROVE_ENDPOINT(snapshotId), { exclude });

// Customer AI profile (contextualization engine) — same single-source rule.
// Backend: analytics_app/urls.py → path("customers/<int:customer_id>/ai-profile/").
export const CUSTOMER_AI_PROFILE_ENDPOINT = (customerId) =>
  `/analytics/customers/${customerId}/ai-profile/`;

export const getCustomerAiProfile = (customerId) =>
  api.get(CUSTOMER_AI_PROFILE_ENDPOINT(customerId));

// `config` merges into the axios request config so a caller can raise `timeout`
// per request (several analytics endpoints run ~20s — longer than the client's
// 20s default — and were aborting). See PestAnalyticsOverview per-tile loading.
export const getRevenueSummary = (params = {}, config = {}) =>
  api.get("/analytics/revenue-summary/", { params, ...config });

export const listPlanningScenarios = (params = {}) =>
  api.get("/analytics/planning/scenarios/", { params });

export const createPlanningScenario = (payload = {}) =>
  api.post("/analytics/planning/scenarios/", payload);

export const updatePlanningScenario = (scenarioId, payload = {}) =>
  api.patch(`/analytics/planning/scenarios/${scenarioId}/`, payload);

export const deletePlanningScenario = (scenarioId) =>
  api.delete(`/analytics/planning/scenarios/${scenarioId}/`);

export const getCostConfiguration = (params = {}) =>
  api.get("/analytics/cost-configuration/", { params });

export const updateCostConfiguration = (payload = {}) =>
  api.patch("/analytics/cost-configuration/", payload);

export const updateCompensationProfile = (userId, payload = {}) =>
  api.patch(`/analytics/compensation-profiles/${userId}/`, payload);

export const getExecutableMetricPreview = (params = {}) =>
  api.get("/analytics/executable-metrics/", { params });

export const runSmartAnalyticsQuery = (params = {}) =>
  api.get("/analytics/smart-query/", { params });

export const saveAnalyticsAudience = (payload = {}) =>
  api.post("/analytics/audiences/save/", payload);

export const getLeadAttributionMetrics = (params = {}) =>
  api.get("/analytics/lead-attribution/", { params });

export const getRevenueDaily = (params = {}) =>
  api.get("/analytics/revenue/daily/", { params });

export const getRevenueMonthly = (params = {}) =>
  api.get("/analytics/revenue/monthly/", { params });

export const getRouteLoad = (params = {}) =>
  api.get("/analytics/route-load/", { params });

export const getTechnicianCapacity = (params = {}, config = {}) =>
  api.get("/analytics/technician-capacity/", { params, ...config });

export const getTechnicianUtilization = (params = {}) =>
  api.get("/analytics/technician-utilization/", { params });

export const getMissedAppointments = (params = {}) =>
  api.get("/analytics/missed-appointments/", { params });

export const getMissedRecovery = (params = {}) =>
  api.get("/analytics/missed-recovery/", { params });

export const getLeadPerformance = (params = {}, config = {}) =>
  api.get("/analytics/lead-performance/", { params, ...config });

export const getSalesRepPerformance = (params = {}) =>
  api.get("/analytics/sales-rep-performance/", { params });

export const getRecurringRevenue = (params = {}, config = {}) =>
  api.get("/analytics/recurring-revenue/", { params, ...config });

export const getContracts = (params = {}, config = {}) =>
  api.get("/analytics/contracts/", { params, ...config });

// Retention is OFF BY DEFAULT per tenant (preferences.cadence.retention); the
// endpoint returns {enabled:false, reason:"off_by_default"} until enabled.
export const getRetention = (params = {}, config = {}) =>
  api.get("/analytics/retention/", { params, ...config });

export const getServiceBreakdown = (params = {}) =>
  api.get("/analytics/service-breakdown/", { params });

export const getOperationalWorkspace = (params = {}) =>
  api.get("/analytics/ontology/workspace/", { params });

export const getPreServiceBrief = (params = {}) =>
  api.get("/analytics/ontology/pre-service-brief/", { params });

export const ingestOperationalMemory = (payload = {}) =>
  api.post("/analytics/ontology/memories/ingest/", payload);

export const executeAnalyticsAction = (actionType, payload = {}) =>
  api.post("/analytics/actions/execute/", {
    action_type: actionType,
    payload,
  });

export const explainAnalyticsSummary = (summary = {}) =>
  api.post("/analytics/voice/explain/", { summary });

// Owner Scorecard (registry + per-tenant targets + live Abon values).
// Backend: analytics_app/urls.py → owner-scorecard/. Returns {enabled:false} for a
// tenant the scorecard is not turned on for — callers render nothing in that case.
export const getOwnerScorecard = (params = {}) =>
  api.get("/analytics/owner-scorecard/", { params });
